import { sectionFromHash } from '../layouts/navConfig';
import { leaveReport, reportsScreenFromHash, REPORTS_SECTION } from '../utils/reportsRoute';
import { closeTopmostModal } from './androidBackStack';

/**
 * Rediseño · Bloque G — el Atrás del teléfono cuando no hay nada abierto encima, «como en una app».
 *
 * Decisión del dueño tras la prueba en teléfono de P-070 (2026-10-07): el Atrás regresaba por cada
 * pantalla visitada, como en una página web, a veces una decena. Ahora, cuando no queda nada que cerrar
 * (un diálogo, el menú, una hoja o un paso del asistente responden antes; ver `androidBackStack.ts`):
 *
 *   · en el Inicio, la app se cierra;
 *   · dentro de un reporte (R1, R2), vuelve a «Reportes», un paso, como «‹ Reportes»;
 *   · en cualquier otra pantalla, va al Inicio.
 *
 * Lo activa el shell mientras está montado, es decir, con la sesión abierta. Sin él (inicio de sesión,
 * consola de plataforma, alta, recuperación), el Atrás sigue como antes: el historial y, sin historial,
 * salir.
 */
export type PhoneBackStep = 'exit' | 'reports' | 'home';

/** Qué hace el Atrás en la pantalla de este hash. Lo que no se reconoce es el Inicio, como en el router. */
export function phoneBackStep(hash: string): PhoneBackStep {
  const section = sectionFromHash(hash);
  if (section === 'dashboard') return 'exit';
  if (section === REPORTS_SECTION && reportsScreenFromHash(hash).screen !== 'landing') return 'reports';
  return 'home';
}

let goHome: (() => void) | null = null;

/** El shell dice cómo se va al Inicio (su navegación de siempre). Devuelve con qué dejar de hacerlo. */
export function setPhoneBackHome(next: () => void): () => void {
  goHome = next;
  return () => { if (goHome === next) goHome = null; };
}

/**
 * El Atrás con nada abierto encima. `none` si el shell no está montado: entonces decide quien llama, como
 * antes.
 */
export function runPhoneBack(): 'handled' | 'exit' | 'none' {
  if (!goHome) return 'none';
  const step = phoneBackStep(window.location.hash);
  if (step === 'exit') return 'exit';
  if (step === 'reports') leaveReport();
  else goHome();
  return 'handled';
}

/**
 * Todo lo que hace el Atrás físico, en orden (lo llama el listener nativo de `App.tsx`):
 *   1. cerrar lo que está más arriba (diálogo, menú, hoja, paso del asistente);
 *   2. con la sesión abierta, lo que diga el shell (`runPhoneBack`);
 *   3. fuera del shell, el historial si lo hay;
 *   4. si no, cerrar la app.
 */
export function handleHardwareBack(canGoBack: boolean, exitApp: () => void): void {
  if (closeTopmostModal()) return;
  const step = runPhoneBack();
  if (step === 'handled') return;
  if (step === 'none' && canGoBack) window.history.back();
  else exitApp();
}
